import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { REHYDRATE, RehydrateAction } from 'redux-persist';
import { RewardStatus, RewardType } from '@/constants/rewards';
import { SurveyAnswer } from '@/types/survey';

type InitialStateSurveyProps = {
  surveyIsCompleted: boolean;
  currentStep: number;
  phoneNumber: string;
  rewards: RewardType[];
  frequencyAnswers: SurveyAnswer[];
  stores: string[];
  feedTags: string[];
};

const REWARDS = [
  { id: 1, name: 'Rewards estimated', points: 50, status: RewardStatus.IN_PROGRESS },
  { id: 2, name: 'Personalize your shopping', points: 100, status: RewardStatus.NO_STARTED },
  { id: 3, name: 'Create an account', points: 100, status: RewardStatus.NO_STARTED },
  { id: 4, name: 'Link bank account', points: 100, status: RewardStatus.NO_STARTED },
];

const initialState: InitialStateSurveyProps = {
  surveyIsCompleted: false,
  currentStep: 0,
  phoneNumber: '',
  rewards: REWARDS,
  frequencyAnswers: [],
  stores: [],
  feedTags: [],
};

export const surveySlice = createSlice({
  name: 'survey',
  initialState,
  reducers: {
    setSurveyIsCompleted: (state, action: PayloadAction<boolean>) => {
      state.surveyIsCompleted = action.payload;
    },
    setCurrentStep: (state, action: PayloadAction<number>) => {
      state.currentStep = action.payload;
    },
    setPhoneNumber: (state, action: PayloadAction<string>) => {
      state.phoneNumber = action.payload;
    },
    setRewardStatus: (state, action: PayloadAction<{ id: number; status: RewardStatus }>) => {
      const index = state.rewards.findIndex((r) => r.id === action.payload.id);

      if (index !== -1) {
        state.rewards[index].status = action.payload.status;
      }
    },
    setAnswer: (state, action: PayloadAction<{ key: 'feedTags' | 'stores'; value: string }>) => {
      const { key, value } = action.payload;
      const index = state[key].indexOf(value);

      if (index !== -1) {
        state[key] = state[key].filter((item) => item !== value);
      } else {
        state[key].push(value);
      }
    },
    addFrequencyAnswer: (state, action: PayloadAction<SurveyAnswer>) => {
      state.frequencyAnswers.push(action.payload);
    },
    clearStore: (state) => {
      state.surveyIsCompleted = false;
      state.currentStep = 0;
      state.phoneNumber = '';
      state.rewards = REWARDS;
      state.frequencyAnswers = [];
      state.stores = [];
      state.feedTags = [];
    },
  },
  extraReducers: (builder) => {
    builder.addCase(REHYDRATE, (state, action: RehydrateAction) => {
      const persisted = (action.payload as { survey?: typeof initialState })?.survey;

      if (!Array.isArray(persisted?.rewards)) state.rewards = REWARDS;
      if (!Array.isArray(persisted?.frequencyAnswers)) state.frequencyAnswers = [];
      if (!Array.isArray(persisted?.feedTags)) state.feedTags = [];
      if (!Array.isArray(persisted?.stores)) state.stores = [];
    });
  },
  selectors: {
    selectSurveyIsCompleted: (state) => state.surveyIsCompleted,
    selectCurrentStep: (state) => state.currentStep,
    selectPhoneNumber: (state) => state.phoneNumber,
    selectStores: (state) => state.stores,
    selectFeedTags: (state) => state.feedTags,
    selectRewards: (state) => state.rewards,
    selectFrequencyAnswers: (state) => state.frequencyAnswers,
  },
});

export const {
  setSurveyIsCompleted,
  setCurrentStep,
  setPhoneNumber,
  setRewardStatus,
  addFrequencyAnswer,
  setAnswer,
  clearStore,
} = surveySlice.actions;
export const {
  selectSurveyIsCompleted,
  selectCurrentStep,
  selectPhoneNumber,
  selectRewards,
  selectFeedTags,
  selectStores,
  selectFrequencyAnswers,
} = surveySlice.selectors;
